import { FC } from 'react';
import { useTranslation } from 'next-i18next';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { cx } from '@emotion/css';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { newrelic } from '@phoenix/core/frontend/shared/ui/newrelic/newrelic';
import { PhoenixIcon } from '@phoenix/design-system/frontend/ui/phoenix-icon/phoenix-icon';
import { ToolImage } from '@phoenix/team-m/my-tool-shed/frontend/ui/common/tool-info/tool-image';
import { ToolInfo } from '@phoenix/team-m/my-tool-shed/frontend/ui/common/tool-info/tool-info';
import { ToolInfoWithLifecycle } from '@phoenix/team-m/my-tool-shed/frontend/ui/common/tool-info/tool-info-with-lifecycle';
import { containerQueries } from '@phoenix/design-system/frontend/ui/breakpoints/breakpoints';
import { DynamicLinkWrapper } from '@phoenix/team-m/dashboard/frontend/shared/dynamic-link-wrapper';
import { ToolInfoLabeledMarks } from '@phoenix/team-m/my-tool-shed/frontend/ui/common/tool-info/tool-info-labeled-marks';
import { ProductListItem } from '@phoenix/team-m/my-tool-shed/frontend/util/product/product.model';

import styles from './tool-shed-products-page.module.scss';

const checkIcon = getDesignSystemIcon('icon-check-16.svg');

export interface ToolShedProductsPageProps {
  products?: ProductListItem[];
  isLoading?: boolean;
  newToolIds?: string[];
}

export const ToolShedProductPage: FC<React.PropsWithChildren<ToolShedProductsPageProps>> = ({
  products: _products,
  isLoading,
  newToolIds = [],
}) => {
  const { t } = useTranslation();

  const products = isLoading ? emptyListArray : _products;

  const onClickProduct = (toolId?: string) => () =>
    toolId &&
    newrelic.addPageAction('toolShedOverview', {
      context: 'productListItem',
      triggerType: 'listItem',
      triggerName: toolId,
      triggerAction: 'click',
    });

  return products?.map((product, i) => {
    const isNew = product.id !== undefined && newToolIds.includes(product.id);
    const key = `productListRow-${product.id ?? i + 1}`;

    return (
      <AnimatePresence key={`${key}-container`} mode="wait">
        {isNew && (
          <motion.div key={`${key}-new-label`} className="mt-8 flex flex-row items-center space-x-3" data-testid="statusNewInShed">
            <PhoenixIcon className="h-4 w-4 text-green" icon={checkIcon} />
            <div className="text-green stihl-text-h5-mobile">{t('toolShed_overview_status_newInShed')}</div>
          </motion.div>
        )}
        <DynamicLinkWrapper
          aria-label={product.productName}
          routePath={product.id && `/tool-shed/tool-details?toolId=${product.id}`}
          onClick={onClickProduct(product.id)}
          data-testid={key}
        >
          <motion.div
            key={key}
            initial="initial"
            animate="visible"
            variants={animationVariants}
            className={cx(!isLoading && styles.productInfo, isNew ? 'mt-2' : 'mt-8')}
          >
            <div className={cx(styles.toolListItem, { [styles.isLoading]: isLoading })}>
              <ToolImage
                product={product}
                isLoading={isLoading}
                imageClassName="aspect-square w-full"
                suppressGird={true}
                sizes={`(max-width: ${containerQueries.tablet}) 100vw, (max-width: ${containerQueries.desktop}) 25vw, 20vw`}
              />
              <ToolInfo
                product={product}
                customContainerClasses="grid gap-y-2 self-center p-6 tablet:p-0"
                isLoading={isLoading}
                children={<ToolInfoLabeledMarks isLoading={isLoading} product={product} className="mt-4" />}
              />
              <ToolInfoWithLifecycle
                product={product}
                isLoading={isLoading}
                customContainerClasses="hidden desktop:grid self-center mr-6"
              />
            </div>
          </motion.div>
        </DynamicLinkWrapper>
      </AnimatePresence>
    );
  });
};

const emptyListArray = Array.from({ length: 4 }).map((_, index) => index);
const animationVariants: Variants = {
  initial: { opacity: 0, backgroundColor: styles.primaryWhite },
  visible: { opacity: 1 },
};
